// =============================================================================
// TypeScript Types - Domain Models
// =============================================================================

// ===========================================================================
// Resource Types
// ===========================================================================

export interface ResourceType {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  isActive: boolean;
  sortOrder: number;
  attributesSchema?: AttributeFieldDefinition[]; // Array di campi custom
  createdAt: string;
  updatedAt: string;
}

// Definizione di un campo attributo nello schema
export interface AttributeFieldDefinition {
  key: string;          // Nome univoco del campo (es: 'skill')
  label: string;        // Label visuale (es: 'Competenza')
  type: 'text' | 'number' | 'boolean' | 'select' | 'date' | 'email' | 'phone';
  required?: boolean;
  placeholder?: string;
  options?: string[];   // Per type: 'select'
  defaultValue?: string | number | boolean;
}

// Schema per attributi dinamici (legacy, per compatibilità)
export interface AttributeSchema {
  type: 'string' | 'number' | 'boolean' | 'select';
  label: string;
  required?: boolean;
  options?: string[]; // Per type: 'select'
  placeholder?: string;
}

export interface Resource {
  id: string;
  organizationId: string;
  name: string;
  code: string | null;
  description: string | null;
  resourceTypeId: string;
  resourceType?: ResourceType;
  color: string | null;
  customAttributes?: Record<string, unknown>; // Attributi dinamici
  isActive: boolean;
  isBookable: boolean;
  /// True se la risorsa è selezionabile esplicitamente dal cliente nel flow pubblico
  isPubliclySelectable?: boolean;
  availabilityPatterns?: AvailabilityPattern[]; // Pattern di disponibilità
  createdAt: string;
  updatedAt: string;
}

// Pattern di disponibilità per una risorsa
export interface AvailabilityPattern {
  id: string;
  dayOfWeek: string | null;  // 'MONDAY', 'TUESDAY', etc.
  startTime: string;          // 'HH:MM' (es. '08:00')
  endTime: string;            // 'HH:MM' (es. '18:00')
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
}

// ===========================================================================
// Service Types
// ===========================================================================

export interface ServiceRequirement {
  id: string;
  resourceTypeId: string;
  resourceType: ResourceType;
  quantity: number;
  isRequired: boolean;
  priority: number;
}

export interface ServiceDefinition {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  description: string | null;
  category: string | null;
  color: string | null;  // Colore HEX per visualizzazione calendario
  requiresClient: boolean; // Se true, richiede un cliente; false per servizi interni
  maxClients: number; // Max clienti per appuntamento (1 = individuale, >1 = servizio di gruppo)
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  basePrice: number | null;
  currency: string;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  requirements?: ServiceRequirement[];
  /// JSONB grezzo dal backend; contiene booking_rules con publicBookingEnabled, ecc.
  config?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Customer Types
// ===========================================================================

export interface Customer {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  fiscalCode: string | null;
  vatNumber: string | null;
  dateOfBirth: string | null;
  internalNotes: string | null;
  isActive: boolean;
  totalAppointments: number;
  pastAppointments: number;
  upcomingAppointments: number;
  lastAppointmentAt: string | null;
  nextAppointmentAt: string | null;
  noShowCount: number;
  cancelledCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Voce dello storico appuntamenti di un cliente */
export interface CustomerAppointment {
  id: string;
  title: string;
  serviceName: string | null;
  color: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: AppointmentStatus;
  price: number;
  resourceNames: string[];
  attendanceRole: 'primary' | 'participant';
  isGroupEvent: boolean;
}

export interface CustomerAppointmentsResponse {
  upcoming: CustomerAppointment[];
  past: CustomerAppointment[];
  upcomingTotal: number;
  pastTotal: number;
}

// ===========================================================================
// Appointment Types
// ===========================================================================

export type AppointmentStatus = 
  | 'DRAFT'
  | 'PENDING'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'RESCHEDULED';

export interface AppointmentAllocation {
  id: string;
  resourceId: string;
  resourceName: string;
  resourceTypeName: string;
  resourceAttributes?: Record<string, unknown>; // Custom attributes della risorsa
  startTime: string;
  endTime: string;
  role: 'primary' | 'support' | 'facility';
}

// Partecipante aggiuntivo di un evento di gruppo (il primo cliente resta su customerId)
export interface AppointmentParticipant {
  id: string;
  customerId: string;
  customerName: string;
  source: 'admin' | 'public';
  createdAt: string;
}

export interface Appointment {
  id: string;
  serviceDefinitionId: string;
  serviceName: string;
  serviceColor?: string;  // Colore HEX del servizio per visualizzazione calendario
  customerId?: string | null;  // Opzionale per servizi interni
  customerName?: string | null;  // Opzionale per servizi interni
  customerEmail?: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: AppointmentStatus;
  allocations?: AppointmentAllocation[];
  // Eventi di gruppo
  maxClients?: number;  // Capienza massima dell'evento (1 = individuale)
  serviceMaxClients?: number | null;  // Default del servizio (per mostrare originale vs override)
  participantCount?: number;  // Occupazione attuale (primario + partecipanti)
  participants?: AppointmentParticipant[];  // Solo nel dettaglio
  metadata?: Record<string, unknown>;
  notes?: string;  // Note opzionali
  createdAt: string;
}

// ===========================================================================
// Slot Types (for availability calculation)
// ===========================================================================

export interface TimeSlot {
  start: Date;
  end: Date;
  isAvailable: boolean;
  resourceId?: string;
}

export interface AvailabilitySlot {
  resourceId: string;
  resourceName: string;
  date: Date;
  slots: TimeSlot[];
}

// ===========================================================================
// API Response Types
// ===========================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

// ===========================================================================
// UI State Types
// ===========================================================================

export interface SchedulerViewState {
  selectedDate: Date;
  selectedResourceTypes: string[];
  viewMode: 'day' | 'week' | 'month';
  zoomLevel: number;
}

export interface AppointmentFormData {
  serviceDefinitionId: string;
  customerId: string;
  scheduledStart: string;
  scheduledEnd: string;
  resourceAllocations: {
    resourceId: string;
    role: 'primary' | 'support' | 'facility';
  }[];
  notes?: string;
}

// ===========================================================================
// Color mappings for status
// ===========================================================================

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  DRAFT: 'bg-gray-200 text-gray-700',
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  RESCHEDULED: 'bg-indigo-100 text-indigo-800',
};

export const APPOINTMENT_STATUS_LABEL_KEYS: Record<AppointmentStatus, string> = {
  DRAFT: 'appointmentStatus.DRAFT',
  PENDING: 'appointmentStatus.PENDING',
  CONFIRMED: 'appointmentStatus.CONFIRMED',
  IN_PROGRESS: 'appointmentStatus.IN_PROGRESS',
  COMPLETED: 'appointmentStatus.COMPLETED',
  CANCELLED: 'appointmentStatus.CANCELLED',
  NO_SHOW: 'appointmentStatus.NO_SHOW',
  RESCHEDULED: 'appointmentStatus.RESCHEDULED',
};
